import React, { useState } from 'react'
import { Check, Car } from 'lucide-react'
import { Modal, ModernButton } from '../ui'
import { VehicleInterestDetailed } from '../../types'

interface VehicleSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (vehicleId: string) => void
  vehicles: VehicleInterestDetailed[]
  leadName: string
}

export const VehicleSelectionModal: React.FC<VehicleSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  vehicles,
  leadName
}) => {
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('')

  // Only show valid VehicleInterestDetailed items (must have the nested .vehicle object)
  const detailedVehicles = vehicles.filter(
    (v): v is VehicleInterestDetailed => !!(v as any).vehicle
  )

  const handleSelect = () => {
    if (selectedVehicleId) {
      onSelect(selectedVehicleId)
      onClose()
    }
  }

  const handleClose = () => {
    setSelectedVehicleId('')
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Selecionar Veículo para Proposta"
      size="lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          O cliente <strong>{leadName}</strong> tem interesse em múltiplos veículos.
          Selecione qual veículo deseja incluir na proposta:
        </p>

        <div className="space-y-3">
          {detailedVehicles.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              <Car className="h-8 w-8 mx-auto mb-2 text-border" />
              Nenhum veículo disponível
            </div>
          ) : (
            detailedVehicles.map((interest) => (
              <div
                key={interest.id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedVehicleId === interest.vehicleId
                    ? 'border-accent bg-accent/5'
                    : 'border-border hover:border-accent/40'
                }`}
                onClick={() => setSelectedVehicleId(interest.vehicleId)}
              >
                <div className="flex items-start gap-3">
                  {/* Foto do veículo */}
                  {interest.vehicle?.photos?.[0]?.url && (
                    <img
                      src={interest.vehicle.photos[0].url}
                      alt={interest.vehicle?.name}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                  )}

                  {/* Informações do veículo */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground">
                      {interest.vehicle?.brand?.name} {interest.vehicle?.model?.name} {(interest.vehicle as any)?.modelYear}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {interest.vehicle?.salePrice ? `Preço: ${interest.vehicle.salePrice}` : ''}
                    </p>
                    {interest.notes && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Obs: {interest.notes}
                      </p>
                    )}
                  </div>

                  {/* Checkbox visual */}
                  <div className="flex-shrink-0">
                    {selectedVehicleId === interest.vehicleId ? (
                      <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 border-2 border-border rounded-full" />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <ModernButton variant="outline" onClick={handleClose}>
            Cancelar
          </ModernButton>
          <ModernButton onClick={handleSelect} disabled={!selectedVehicleId}>
            Criar Proposta
          </ModernButton>
        </div>
      </div>
    </Modal>
  )
}
